// Markdown.jsx — render a notes/description field as markdown, safely.
//
// Alex, 2026-08-21: the `notes` columns (catalog_lines/families/categories,
// products — 1,397 filled rows) are unread anywhere in the app. Existing rows
// are plain prose written on import; going forward Alex or a session writing
// notes while working can use markdown structure (lists, emphasis) and have it
// actually render. This is that renderer, not the card that places it — the
// card is per-selection (category/family/product) and belongs wherever it is
// mounted; this component only turns a string into readable output.
//
// WHY A STRING RENDERS SAFELY WITHOUT BREAKING EXISTING NOTES: CommonMark's own
// flanking-delimiter rule already protects the legacy rows — `_` only opens
// emphasis at a WORD BOUNDARY, so provenance text like `Sub_FabX` or
// `Elec_Inst_Hrs` (checked against all 1,397 filled rows before picking this
// library) passes through as plain text, unmangled. Verified directly against
// markdown-to-jsx, not assumed from the spec.
//
// disableParsingRawHTML: true — this field is edited by more than one person
// over time (import data + whoever works the row later); raw `<...>` in a note
// (a size spec like "< 3 inch", say) must read as text, never as a tag.
//
// No raw/rendered toggle. Nothing today needs one — legacy prose renders as
// itself since a plain paragraph is valid markdown. Add the toggle only if a
// real row is found where rendering reads wrong; building it now would be
// solving a problem that hasn't happened yet.
import MarkdownToJsx from 'markdown-to-jsx';

// A link inside the app (`/go/prod:713`, the chat's citations) navigates in place; an
// outside link opens a new tab so the app is never lost.
function Link({ href = '', children, ...rest }) {
  const internal = href.startsWith('/');
  return (
    <a {...rest} href={href} style={{ color: 'var(--th-accent)' }}
       {...(internal ? null : { target: '_blank', rel: 'noopener noreferrer' })}>
      {children}
    </a>
  );
}

// One set of overrides per text size: 'body' for notes cards, 'small' for the chat.
function optionsFor(size) {
  const fs = size === 'small' ? 'var(--fs-small)' : 'var(--fs-body)';
  const text = { color: 'var(--th-text-body)', fontSize: fs };
  const code = { fontFamily: 'var(--ff-mono)', fontSize: fs, background: 'var(--surface-sunken)', padding: '1px 4px', borderRadius: 3 };
  const cell = { border: '1px solid var(--border-default)', padding: '2px 6px' };
  return {
    disableParsingRawHTML: true,
    overrides: {
      p:  { props: { style: { margin: '0 0 6px', ...text } } },
      ul: { props: { style: { margin: '0 0 6px', paddingLeft: 18, ...text } } },
      ol: { props: { style: { margin: '0 0 6px', paddingLeft: 18, ...text } } },
      li: { props: { style: { marginBottom: 2 } } },
      h1: { props: { style: { margin: '8px 0 4px', fontWeight: 700, ...text } } },
      h2: { props: { style: { margin: '8px 0 4px', fontWeight: 700, ...text } } },
      h3: { props: { style: { margin: '8px 0 4px', fontWeight: 700, ...text } } },
      h4: { props: { style: { margin: '8px 0 4px', fontWeight: 700, ...text } } },
      code: { props: { style: code } },
      pre: { props: { style: { margin: '0 0 6px', padding: '8px 10px', borderRadius: 6, overflowX: 'auto', background: 'var(--surface-sunken)', color: 'var(--text-primary)' } } },
      table: { props: { style: { borderCollapse: 'collapse', margin: '0 0 6px', display: 'block', overflowX: 'auto', ...text } } },
      th: { props: { style: cell } },
      td: { props: { style: cell } },
      blockquote: { props: { style: { margin: '0 0 6px', paddingLeft: 8, borderLeft: '3px solid var(--border-strong)', color: 'var(--text-secondary)' } } },
      a:  { component: Link },
    },
  };
}
const OPTIONS = { body: optionsFor('body'), small: optionsFor('small') };

// Markdown({ text }): null for empty/missing (same "render nothing" contract as
// Notice.jsx), otherwise the parsed markdown. `text` — not `children` — so a
// caller passing a possibly-null DB field never needs its own guard.
export default function Markdown({ text, style, size = 'body' }) {
  if (!text || !String(text).trim()) return null;
  return (
    <div style={style}>
      <MarkdownToJsx options={OPTIONS[size] || OPTIONS.body}>{String(text)}</MarkdownToJsx>
    </div>
  );
}
